import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { cloudVariant } from "./cloud";
import logo from "./logo.svg";
import {
  OCI_SIGNING_KEY_SETUP,
  ociIdentityItems,
  ociPrivateKeyItem,
  ociSigningParams,
} from "./signingKey";

const aiVariant = productVariant({
  authLabel: "Generative AI (API signing key)",
  identifier: "oracleOci",
  protocol: "http",
  defaultUrlPattern: () =>
    "https://inference\\.generativeai\\.[^/]*\\.oci\\.oraclecloud\\.com/.*",
  auth: (app) =>
    customAuth("oci", {
      ...ociSigningParams(app),
      compartmentId: app.config.compartmentId,
    }),
  steps: [
    {
      title: "Create an OCI API signing key",
      items: [{ kind: "markdown", content: OCI_SIGNING_KEY_SETUP }],
    },
    {
      title: "Enter your OCI credentials",
      items: [
        ...ociIdentityItems,
        {
          kind: "input",
          label: "Compartment OCID",
          placeholder: "ocid1.compartment.oc1..…",
          required: true,
          target: { kind: "configuration", field: "compartmentId" },
          helperText:
            "The compartment that holds your Generative AI access; sent as opc-compartment-id.",
        },
        ociPrivateKeyItem,
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          // No fetch: OCI serves no model-list path on the inference host, so
          // these are typed by hand.
          columns: [{ field: "name", placeholder: "cohere.command-r-plus" }],
          addLabel: "Add model",
          helperText: `${MODELS_HELPER_TEXT} OCI model IDs are vendor-prefixed; copy them from the model list in the OCI console.`,
        },
      ],
    },
  ],
  // A GET on the chat path returns 400 ("Content parsing error") once the
  // request is signed, but 404 ("Authorization failed") when the signature is
  // rejected — so a 400 confirms the credentials sign valid OCI requests.
  connectionTest: {
    protocol: "http",
    request: ({ region }) => ({
      url: `https://inference.generativeai.${region}.oci.oraclecloud.com/20231130/actions/v1/chat/completions`,
      expectedStatus: 400,
    }),
  },
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Call the OCI Generative AI OpenAI-compatible endpoint at /20231130/actions/v1/chat/completions. Pass a placeholder API key; the proxy signs each request with the connected OCI credentials.",
      configuration
    ),
  usage: openaiUsage(),
});

export const oracleOci = defineProduct({
  name: "Oracle Cloud Infrastructure",
  tagLine: "Cloud infrastructure and Generative AI",
  logo,
  tags: ["ai", "it", "engineering"],
  keywords: "oci oracle cloud infrastructure",
  variants: [aiVariant, cloudVariant],
});
